import { useState, useRef, useEffect } from 'react'
import { Bell, LogOut, Users, Clock, Moon, Sun, UtensilsCrossed } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/Button'
import { useNotificationStore } from '@/lib/store/useNotificationStore'
import { OfflineIndicator } from '@/components/ui/OfflineIndicator'
import { SyncProgressBar } from '@/components/ui/SyncProgressBar'
import { useThemeStore } from '@/lib/store/useThemeStore'
import { format, isToday } from 'date-fns'

export function Topbar() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const { notifications, unreadCount, markAllRead } = useNotificationStore()
  const resolvedTheme = useThemeStore((s) => s.resolvedTheme)
  const toggleTheme = useThemeStore((s) => s.toggleTheme)
  const [open, setOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handleOpen = () => {
    setOpen((v) => !v)
    if (!open) markAllRead()
  }

  const handleGoToBooking = (documentId: string) => {
    setOpen(false)
    navigate(`/bookings/${documentId}`)
  }

  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <>
    <SyncProgressBar />
    <header className="sticky top-0 z-30 flex h-16 min-w-0 items-center justify-between border-b border-[var(--color-border-subtle)] bg-[var(--color-bg-base)]/90 px-3 backdrop-blur-xl sm:px-6">
      <div className="flex min-w-0 items-center gap-2 md:hidden">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-[var(--color-accent-amber-dim)]">
          <UtensilsCrossed className="h-5 w-5 text-[var(--color-accent-amber)]" />
        </div>
        <span className="font-display text-base font-semibold text-[var(--color-text-primary)]">Ordevo</span>
      </div>

      <div className="ml-auto flex min-w-0 items-center gap-1 sm:gap-2">

        <OfflineIndicator />

        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          title={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-label={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {resolvedTheme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>

        {/* Bell */}
        <div className="relative" ref={panelRef}>
          <button
            type="button"
            onClick={handleOpen}
            aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
            aria-expanded={open}
            className="relative flex h-10 w-10 items-center justify-center rounded-[var(--radius-md)] text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)]"
          >
            <Bell className="h-4 w-4" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white leading-none">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {open && (
            <div className="absolute right-0 top-12 z-50 max-h-[calc(100dvh-5rem)] w-[min(calc(100vw-1rem),20rem)] overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] shadow-xl">
              <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border-subtle)]">
                <span className="text-sm font-semibold text-[var(--color-text-primary)]">Notifications</span>
                {notifications.length > 0 && (
                  <button
                    type="button"
                    onClick={() => useNotificationStore.getState().clear()}
                    className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] transition-colors"
                  >
                    Clear all
                  </button>
                )}
              </div>

              {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-[var(--color-text-muted)]">
                  <Bell className="h-6 w-6 mb-2 opacity-40" />
                  <p className="text-sm">No notifications yet</p>
                </div>
              ) : (
                <div className="max-h-[min(24rem,calc(100dvh-9rem))] overflow-y-auto overscroll-contain divide-y divide-[var(--color-border-subtle)]">
                  {notifications.map((n) => (
                    <button
                      type="button"
                      key={n.id}
                      onClick={() => handleGoToBooking(n.documentId)}
                      className="w-full text-left px-4 py-3 hover:bg-[var(--color-bg-elevated)] transition-colors"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div className="h-2 w-2 rounded-full bg-[var(--color-accent-amber)] shrink-0" />
                        <span className="text-sm font-medium text-[var(--color-text-primary)] truncate">
                          {n.guestName}
                        </span>
                        <span className="ml-auto text-xs text-[var(--color-text-muted)] shrink-0">
                          {isToday(n.createdAt)
                            ? format(n.createdAt, 'HH:mm')
                            : format(n.createdAt, 'MMM d')}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 pl-4 text-xs text-[var(--color-text-muted)]">
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{n.time}</span>
                        <span className="flex items-center gap-1"><Users className="h-3 w-3" />{n.people} {n.people === 1 ? 'person' : 'people'}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="ml-1 flex items-center gap-1 border-l border-[var(--color-border-subtle)] pl-2 sm:ml-2 sm:gap-2">
          <div className="h-8 w-8 rounded-full bg-[var(--color-accent-amber-dim)] flex items-center justify-center text-xs font-semibold text-[var(--color-accent-amber)]">
            {(user?.fullName?.charAt(0) || user?.email?.charAt(0) || 'U').toUpperCase()}
          </div>
          <Button variant="ghost" size="icon" onClick={handleLogout} title="Log out" aria-label="Log out">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </header>
    </>
  )
}
